export type ObjectLabelConfig = {
    /**
     * Подпись, которая выводится перед значением в UI (только на стороне клиента).
     * Если не задана, будет выводиться только значение.
     */
    caption?: string
    /**
     * Имя поля объекта (после форматирования), из которого берётся значение.
     */
    field: string
}

/**
 * Клиентская конфигурация: какое поле показывать в "лейбле" узла для объектов на карте.
 */
export const PROPERTIES_FOR_OBJECT_ON_MAP_NODE_LABEL: Record<string, ObjectLabelConfig> = {
    RoadLandLots: {
        caption: "Кадастровый номер",
        field: "CadastralNumber"
    },
    AdjacentLandLots: {
        caption: "Кадастровый номер",
        field: "CadastralNumber"
    },
    LandSubLots: {
        caption: "Кадастровый номер",
        field: "CommonCadastralNumber"
    },
    LandSubLotObjects: {
        caption: "Кадастровый номер",
        field: "CommonCadastralNumber"
    },
    PowerLines6: {
        field: "Name"
    },
    PowerLines10: {
        field: "Name"
    },
    PowerSubStations: {
        field: "Name"
    }
}

/**
 * Клиентская конфигурация: какое поле показывать в заголовке карточки объекта (по типу объекта).
 *
 * Пример:
 * PowerSubStations: { caption: "Адрес", field: "Address" }
 * -> "Подстанция — Адрес: <значение>"
 */
export const PROPERTIES_FOR_OBJECT_INFO_CARD_HEADER_LABEL: Record<string, ObjectLabelConfig> = {
    PowerSubStations: { field: "Name" }
}

/**
 * Клиентская конфигурация: какое поле показывать в заголовке страницы карты объекта (по типу объекта).
 *
 * Пример:
 * PowerSubStations: { caption: "Адрес", field: "Address" }
 * -> "Подстанция — Адрес: <значение>"
 */
export const PROPERTIES_FOR_OBJECT_MAP_HEADER_LABEL: Record<string, ObjectLabelConfig> = {
    PowerSubStations: { field: "Name" }
}

type BuildObjectCardHeaderTitleArgs = {
    objectTypeName?: string
    objectTypeCaptionSingular?: string | null
    formattedObjectInfo?: Record<string, any> | null
    /**
     * Конфиг (по типу объекта), из которого берётся поле/подпись для заголовка.
     * Если не задан, используется конфиг карточки объекта.
     */
    configByType?: Record<string, ObjectLabelConfig>
}

/**
 * Приводит произвольное значение к форматированной строке для UI.
 * - string -> trim
 * - number/boolean/bigint -> String(...)
 * - прочее -> String(...), но отсекаем бессмысленное "[object Object]"
 */
function normalizeUiText(value: unknown): string | undefined {
    if (value == null) return undefined

    if (typeof value === "string") {
        const s = value.trim()
        return s || undefined
    }

    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
        const s = String(value).trim()
        return s || undefined
    }

    // fallback
    const s = String(value).trim()
    if (!s || s === "[object Object]") return undefined
    return s
}

/**
 * Формирует заголовок объекта на основе типа и настроенного поля (по конфигу).
 *
 * Логика:
 * 1. Если нет названия типа (captionSingular) → возвращает значение поля или пустую строку
 * 2. Если нет значения поля → возвращает только название типа
 * 3. Если есть и название типа, и значение → формирует строку вида:
 *    - "Название типа — Подпись: значение" (если задана подпись)
 *    - "Название типа — значение" (если подпись не задана)
 *
 * Конфиг берётся из `configByType` (если передан), иначе используется
 * `PROPERTIES_FOR_OBJECT_INFO_CARD_HEADER_LABEL`.
 */
export function buildObjectCardHeaderTitle(args: BuildObjectCardHeaderTitleArgs): string {
    const { objectTypeName, objectTypeCaptionSingular, formattedObjectInfo, configByType } = args

    // Нормализуем название типа объекта (captionSingular)
    const typeName = normalizeUiText(objectTypeCaptionSingular)

    // Получаем конфигурацию для данного типа объекта
    const configSource = configByType ?? PROPERTIES_FOR_OBJECT_INFO_CARD_HEADER_LABEL
    const config = objectTypeName ? configSource?.[objectTypeName] : undefined

    // Извлекаем значение из настроенного поля (если конфигурация есть)
    const fieldName = config?.field
    const fieldValue = fieldName ? formattedObjectInfo?.[fieldName] : undefined
    const normalizedValue = normalizeUiText(fieldValue)

    // Если нет названия типа, возвращаем только значение поля (или пустую строку)
    if (!typeName) {
        return normalizedValue ?? ""
    }

    // Если нет значения поля, возвращаем только название типа
    if (!normalizedValue) {
        return typeName
    }

    // Формируем полный заголовок: "Название типа — [Подпись: ]значение"
    const caption = normalizeUiText(config?.caption)
    return caption ? `${typeName} — ${caption}: ${normalizedValue}` : `${typeName} — ${normalizedValue}`
}
